import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';

const STATIC_COMMANDS = [
  { type: 'page', title: 'Dashboard', subtitle: 'Overview & analytics', icon: 'fa-chart-pie', path: '/' },
  { type: 'page', title: 'Lead Database', subtitle: 'All your leads', icon: 'fa-address-book', path: '/leads' },
  { type: 'page', title: 'Permit Scanner', subtitle: 'Scan for permits', icon: 'fa-tower-broadcast', path: '/scanner' },
  { type: 'page', title: 'Lead Map', subtitle: 'Geographic view', icon: 'fa-map-location-dot', path: '/map' },
  { type: 'page', title: 'Analytics', subtitle: 'Performance metrics', icon: 'fa-chart-line', path: '/analytics' },
  { type: 'page', title: 'Proposals', subtitle: 'Manage proposals', icon: 'fa-file-invoice', path: '/proposals' },
  { type: 'page', title: 'Lead Mining', subtitle: 'Mining jobs', icon: 'fa-satellite-dish', path: '/mining' },
  { type: 'page', title: 'Auto-Pilot', subtitle: 'Automated scanning', icon: 'fa-robot', path: '/autopilot' },
  { type: 'page', title: 'Credit Store', subtitle: 'Buy credits', icon: 'fa-coins', path: '/credits' },
  { type: 'page', title: 'Campaigns', subtitle: 'Email campaigns', icon: 'fa-bullhorn', path: '/campaigns' },
  { type: 'page', title: 'Leaderboard', subtitle: 'Team rankings', icon: 'fa-ranking-star', path: '/leaderboard' },
  { type: 'page', title: 'Lead Scoring', subtitle: 'ML scoring model', icon: 'fa-brain', path: '/scoring' },
  { type: 'page', title: 'Settings', subtitle: 'Your preferences', icon: 'fa-sliders', path: '/settings' },
  { type: 'page', title: 'Install Pipeline', subtitle: 'Signed → powered on', icon: 'fa-helmet-safety', path: '/projects' },
  { type: 'page', title: 'Templates', subtitle: 'Proposal templates', icon: 'fa-palette', path: '/templates' },
  { type: 'page', title: 'Webhooks', subtitle: 'CRM integrations', icon: 'fa-plug', path: '/webhooks' },
  { type: 'page', title: 'Referrals', subtitle: 'Referral program', icon: 'fa-gift', path: '/referrals' },
  { type: 'action', title: 'Add New Lead', subtitle: 'Create a lead manually', icon: 'fa-user-plus', action: 'create-lead' },
  { type: 'action', title: 'Export Leads CSV', subtitle: 'Download all leads', icon: 'fa-file-export', action: 'export-csv' },
  { type: 'action', title: 'Toggle Dark Mode', subtitle: 'Switch theme', icon: 'fa-circle-half-stroke', action: 'toggle-theme' },
];

export default function CommandPalette({ isOpen, onClose, onAction }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(0);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelected(0);
      setResults(STATIC_COMMANDS.slice(0, 8));
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const searchRemote = useCallback(async (q) => {
    if (q.length < 2) return;
    setSearching(true);
    try {
      const res = await api.get('/api/search/global', { params: { q } });
      return res.data.results || [];
    } catch { return []; }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    if (!query) {
      setResults(STATIC_COMMANDS.slice(0, 8));
      setSelected(0);
      return;
    }
    const filtered = STATIC_COMMANDS.filter(c =>
      c.title.toLowerCase().includes(query.toLowerCase()) ||
      c.subtitle.toLowerCase().includes(query.toLowerCase())
    );

    const timer = setTimeout(async () => {
      const remote = await searchRemote(query);
      setResults([...filtered, ...remote]);
      setSelected(0);
    }, 300);

    setResults(filtered);
    setSelected(0);
    return () => clearTimeout(timer);
  }, [query, searchRemote]);

  const executeCommand = (cmd) => {
    onClose();
    if (cmd.path) {
      navigate(cmd.path);
    } else if (cmd.action) {
      if (onAction) onAction(cmd.action);
    } else if (cmd.type === 'lead') {
      navigate(`/leads/${cmd.id}`);
    } else if (cmd.type === 'proposal') {
      navigate(`/proposals`);
    } else if (cmd.type === 'campaign') {
      navigate(`/campaigns`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected(s => Math.min(s + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected(s => Math.max(s - 1, 0));
    } else if (e.key === 'Enter' && results[selected]) {
      executeCommand(results[selected]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] cmd-backdrop flex items-start justify-center pt-[15vh]" onClick={onClose} data-testid="command-palette">
      <div className="w-full max-w-xl bg-surface border border-border rounded-2xl shadow-2xl overflow-hidden animate-scale-in" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <i className={`fa-solid ${searching ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'} text-text-muted text-sm`} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search leads, pages, actions..."
            className="flex-1 bg-transparent text-text-primary text-base outline-none placeholder:text-text-muted"
            data-testid="command-palette-input"
          />
          <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-md bg-surface-alt border border-border text-[10px] font-mono font-bold text-text-muted">ESC</kbd>
        </div>
        <div className="max-h-[340px] overflow-y-auto py-2">
          {results.length === 0 ? (
            <div className="py-10 text-center">
              <i className="fa-solid fa-search text-2xl text-text-muted opacity-20 mb-2 block" />
              <p className="text-sm text-text-muted">No results found</p>
            </div>
          ) : results.map((cmd, i) => (
            <button
              key={`${cmd.type}-${cmd.title}-${i}`}
              className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${i === selected ? 'bg-primary/10 text-primary' : 'hover:bg-surface-alt text-text-primary'}`}
              onClick={() => executeCommand(cmd)}
              onMouseEnter={() => setSelected(i)}
              data-testid={`cmd-${cmd.title?.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${i === selected ? 'bg-primary/20 text-primary' : 'bg-surface-alt text-text-muted'}`}>
                <i className={`fa-solid ${cmd.icon || 'fa-circle'} text-sm`} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{cmd.title}</p>
                <p className="text-[11px] text-text-muted truncate">{cmd.subtitle}</p>
              </div>
              {cmd.type === 'page' && <span className="text-[9px] font-bold uppercase tracking-wider text-text-muted bg-surface-alt px-2 py-0.5 rounded-md">Page</span>}
              {cmd.type === 'action' && <span className="text-[9px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-md">Action</span>}
              {cmd.type === 'lead' && <span className="text-[9px] font-bold uppercase tracking-wider text-accent bg-accent/10 px-2 py-0.5 rounded-md">Lead</span>}
            </button>
          ))}
        </div>
        <div className="px-5 py-2.5 border-t border-border bg-surface-alt flex items-center gap-4 text-[10px] text-text-muted font-medium">
          <span><kbd className="font-mono font-bold">↑↓</kbd> navigate</span>
          <span><kbd className="font-mono font-bold">↵</kbd> select</span>
          <span><kbd className="font-mono font-bold">esc</kbd> close</span>
          <span className="ml-auto"><kbd className="font-mono font-bold">Cmd+K</kbd></span>
        </div>
      </div>
    </div>
  );
}
